import { useQuery } from '@apollo/client';
import {
  currentOrderWithPrescriptions,
  CurrentOrderWithPrescriptionsQueryParams,
  CurrentOrderWithPrescriptionsQueryResponse,
} from '@queries/order/queries/currentOrderWithPrescriptions';
import { useAuth } from './useAuth';
import {
  lookForPrescriptions,
  LookForPrescriptionsResponse,
} from '@queries/prescription/queries/lookForPrescriptions';
import { usePermissions } from './usePermissions';

export function usePendingPrescriptions() {
  const { appUser } = useAuth();
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const isAssistantUser = userCan('MANAGE_USER');
  const { data: prescriptionsData, loading: prescriptionsLoading } = useQuery<
    CurrentOrderWithPrescriptionsQueryResponse,
    CurrentOrderWithPrescriptionsQueryParams
  >(currentOrderWithPrescriptions(), {
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    skip: !appUser,
  });
  const productsWithPrescription = prescriptionsData?.currentOrder?.orderDetails
    .filter((detail) => detail.product.prescriptionType)
    .map((detail) => detail.product);
  const {
    data: lookForPrescriptionsData,
    loading: prescriptionRequiredLoading,
  } = useQuery<LookForPrescriptionsResponse>(lookForPrescriptions(), {
    fetchPolicy: 'cache-and-network',
    skip: !appUser,
  });
  if (isLocalVendor || isAssistantUser)
    return {
      requirePrescriptions: false,
      enoughAssociationsFound: true,
      requirePrescriptionUpload: false,
      loading: false,
      pendingPrescriptions: prescriptionsData?.pendingPrescriptions ?? [],
      associatedPrescriptions: [],
    };
  const requirePrescriptions = productsWithPrescription?.length ?? 0;
  const requirePrescriptionUpload = productsWithPrescription?.some(
    (product) => product.prescriptionType !== 'NOT_REQUIRED',
  );
  const associationsAmount =
    lookForPrescriptionsData?.lookForPrescriptions?.associations?.length ?? 0;
  const enoughAssociationsFound = associationsAmount == requirePrescriptions;
  return {
    requirePrescriptions,
    enoughAssociationsFound,
    requirePrescriptionUpload,
    loading: prescriptionsLoading || prescriptionRequiredLoading,
    pendingPrescriptions: prescriptionsData?.pendingPrescriptions ?? [],
    associatedPrescriptions:
      lookForPrescriptionsData?.lookForPrescriptions.associations.map(
        (association) => association.prescription,
      ) ?? [],
  };
}
